/**
 * Flat shape of a saved pipe feature's `properties` JSONB column. Mirrors the
 * stockpile `feature.ts` conventions: insertion order = display order (name,
 * headline metrics, then quality signals, then provenance, `kind` last), and
 * numbers rounded to 4 decimals for readable JSON / attribute tables.
 *
 * The centerline geometry (start/end) is stored as the Feature's `LineString`
 * geometry, not here. These properties carry everything else needed to
 * re-render the pipe as a tube and re-enter edit mode (radius + axis + centre).
 */

import type { Vec3 } from './types';

export interface PipeFeatureProperties {
  name: string;

  // ── Headline metrics ─────────────────────────────────────────────────
  diameterM: number;
  radiusM: number;
  lengthM: number;

  // ── Geometry (axis + centre; endpoints live in the LineString) ────────
  axisX: number;
  axisY: number;
  axisZ: number;
  centerX: number;
  centerY: number;
  centerZ: number;

  // ── Quality / confidence signals ─────────────────────────────────────
  arcCoverageDeg: number;
  rmsM: number;
  inlierCount: number;
  confidence: number;

  // ── Provenance ───────────────────────────────────────────────────────
  sourceLayerId: string;
  computedAt: number;

  kind: 'PIPE';
}

const round4 = (v: number): number => (Number.isFinite(v) ? Math.round(v * 10000) / 10000 : v);

export function buildPipeProperties(input: {
  name: string;
  radiusM: number;
  lengthM: number;
  axis: Vec3;
  center: Vec3;
  arcCoverageDeg: number;
  rmsM: number;
  inlierCount: number;
  confidence: number;
  sourceLayerId: string;
  computedAt: number;
}): PipeFeatureProperties {
  return {
    name: input.name,
    diameterM: round4(input.radiusM * 2),
    radiusM: round4(input.radiusM),
    lengthM: round4(input.lengthM),
    axisX: round4(input.axis[0]),
    axisY: round4(input.axis[1]),
    axisZ: round4(input.axis[2]),
    centerX: round4(input.center[0]),
    centerY: round4(input.center[1]),
    centerZ: round4(input.center[2]),
    arcCoverageDeg: Math.round(input.arcCoverageDeg),
    rmsM: round4(input.rmsM),
    inlierCount: Math.round(input.inlierCount),
    confidence: round4(input.confidence),
    sourceLayerId: input.sourceLayerId,
    computedAt: input.computedAt,
    kind: 'PIPE',
  };
}

export function parsePipeProperties(raw: unknown): PipeFeatureProperties | null {
  if (raw == null || typeof raw !== 'object') return null;
  const p = raw as Record<string, any>;
  if (p.kind !== 'PIPE') return null;
  const num = (v: unknown, fb = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fb);
  return {
    name: typeof p.name === 'string' ? p.name : '',
    diameterM: num(p.diameterM),
    radiusM: num(p.radiusM),
    lengthM: num(p.lengthM),
    axisX: num(p.axisX),
    axisY: num(p.axisY),
    axisZ: num(p.axisZ, 1),
    centerX: num(p.centerX),
    centerY: num(p.centerY),
    centerZ: num(p.centerZ),
    arcCoverageDeg: num(p.arcCoverageDeg),
    rmsM: num(p.rmsM),
    inlierCount: num(p.inlierCount),
    confidence: num(p.confidence),
    sourceLayerId: typeof p.sourceLayerId === 'string' ? p.sourceLayerId : '',
    computedAt: num(p.computedAt),
    kind: 'PIPE',
  };
}
